import { notify } from "notiwind"
import { isRef, watchEffect } from "vue"
import type { Ref } from "vue"
import type { ApolloQueryResult } from "@apollo/client"
import type { GraphQLFormattedError } from "graphql/error"
import { createLogger } from "./logger"
import { VersionConflictError } from "./forms/versionConflict"

const logger = createLogger('Notification')

type Notification = { title?: string; text: string; details?: string } | string
type DescribedRefError =
    | Ref<Error | null | undefined>
    | { error: Ref<Error | null | undefined>; description: string }
type DescribedError =
    | Error
    | GraphQLFormattedError
    | null
    | undefined
    | { error?: Error | null; description: string }
    | Notification

export function notifySuccess(notification: Notification, time?: number) {
    if ("string" === typeof notification) {
        notify(
            {
                group: "app",
                kind: "success",
                text: notification,
            },
            time || 4000
        )
    } else {
        notify(
            {
                group: "app",
                kind: "success",
                ...notification,
            },
            time || 4000
        )
    }
}

export function notifyError(notification: DescribedError, time?: number) {
    if (notification instanceof VersionConflictError) return

    if (notification) {
        // Info, nicht error: der Toast ist UI-Feedback, der zugrunde liegende Fehler ist an
        // seiner Quelle bereits gemeldet (Apollo errorLink bzw. captureException am
        // Call-Site). Ein error-Log wuerde ihn ueber die Roarr-Sentry-Bridge als nacktes
        // Toast-Objekt {title, text, details} ein zweites Mal melden.
        logger.info({ error: notification }, 'error notification shown')

        if ("string" === typeof notification) {
            notify(
                {
                    group: "app",
                    kind: "error",
                    text: notification,
                },
                time || 10000
            )
        } else if (notification instanceof Error || "message" in notification) {
            const title = "name" in notification ? notification.name : undefined
            const text = notification.message

            notify(
                {
                    group: "app",
                    kind: "error",
                    title,
                    text,
                },
                time || 10000
            )
        } else if ("error" in notification && "description" in notification) {
            const error = notification.error
            if (error) {
                const title = error.name
                const text = notification.description
                const details = error.message

                notify(
                    {
                        group: "app",
                        kind: "error",
                        title,
                        text,
                        details,
                    },
                    time || 10000
                )
            }
        } else {
            notify(
                {
                    group: "app",
                    kind: "error",
                    ...notification,
                },
                time || 10000
            )
        }
    }
}

export function notifyErrors(
    errors: readonly (DescribedError | undefined)[] | undefined
) {
    if (errors) {
        errors.forEach((error) => {
            notifyError(error)
        })
    }
}

export function watchErrors(...args: DescribedRefError[]) {
    args.forEach((arg) => {
        // Dedupe repeated identical errors so a persistent/polling failure (e.g. a
        // query with `pollInterval` that keeps returning the same "Access Denied")
        // notifies once instead of flooding the UI and Sentry on every tick. Re-arms
        // once the error clears, so a later recurrence is reported again.
        let lastNotifiedKey: string | undefined
        watchEffect(() => {
            const ref = isRef(arg) ? arg : arg.error
            const error = ref.value
            if (!error) {
                lastNotifiedKey = undefined
                return
            }

            const key = `${error.name} ${error.message}`
            if (key === lastNotifiedKey) {
                return
            }
            lastNotifiedKey = key

            const title = error.name
            const text = "description" in arg ? arg.description : error.message
            const details = "description" in arg ? error.message : ""

            notifyError({ title, text, details })
        })
    })
}

export function catchErrors<T>(
    fallbackValue?: T
): (reason: Error) => T | undefined {
    return (reason: Error) => {
        notifyError(reason)
        return fallbackValue
    }
}

export function handleGraphQlErrorsOrReturnData<T>(result: ApolloQueryResult<T>): T {
  if (result.error) {
    notifyError(result.error)
    throw result.error
  } else if (result.errors) {
    notifyErrors(result.errors)
    throw new Error(result.errors.map(e => e.message).join(", "))
  } else {
    return result.data
  }
}
